import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import PropTypes, { InferProps } from 'prop-types';
import { mediumRoundedCorners } from './RoundedCorners';

const ImagesContainer = styled.div`
  width: 100%;
  height: inherit;
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
`;

const ImageContainer = styled.div<{
  active?: boolean;
  index: number;
  backgroundImageUrl?: string | null;
}>`
  flex: 0 0 100%;
  visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
  ${({ index }) => (`transform: translateX(-${index * 100}%)`)};
  background-image: ${({ backgroundImageUrl }) => backgroundImageUrl
    && `url(${backgroundImageUrl})`};
  background-size: 100%;
  display: flex;
  align-items: stretch;
  ${mediumRoundedCorners};
  overflow: hidden;
`;

const BackDropContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  width: 100%;
  backdrop-filter: blur(100px);
`;

const imageSliderPropertyTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
    image: PropTypes.node.isRequired,
    backgroundImageUrl: PropTypes.string,
  }).isRequired).isRequired,
  interval: PropTypes.number,
  className: PropTypes.string,
};

type ImageSliderProperties = InferProps<typeof imageSliderPropertyTypes> & {
  className?: string;
  interval?: number;
};

const ImageSlider = ({
  className,
  images,
  interval = 1000,
}: ImageSliderProperties) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [intervalId, setIntervalId] = useState<number | undefined>();
  const { length } = images;

  const handleMouseEnter = useCallback(() => {
    if (length > 1) {
      const newIntervalId = window.setInterval(() => {
        setActiveIndex(
          (currentActiveIndex) => (currentActiveIndex + 1) % length,
        );
      }, interval);

      setIntervalId(newIntervalId);
    }
  }, [interval, length]);

  const handleMouseLeave = useCallback(() => {
    if (intervalId) {
      clearInterval(intervalId);

      setIntervalId(undefined);
    }
  }, [intervalId]);

  useEffect(() => () => {
    clearInterval(intervalId);
  }, [intervalId]);

  return (
    <ImagesContainer
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={className}
    >
      {images.map(({ image, backgroundImageUrl, key }, index) => (
        <ImageContainer
          key={key}
          active={activeIndex === index}
          index={index}
          backgroundImageUrl={backgroundImageUrl}
          data-testid="image-container"
        >
          <BackDropContainer>{image}</BackDropContainer>
        </ImageContainer>
      ))}
    </ImagesContainer>
  );
};

ImageSlider.propTypes = imageSliderPropertyTypes;

export default ImageSlider;
